import { useCallback } from "react";

const PIN_STORAGE_KEY_PREFIX = "kidmode_pin_";

function storageKey(clerkUserId: string) {
  return `${PIN_STORAGE_KEY_PREFIX}${clerkUserId}`;
}

/**
 * PIN guard for Kid Mode.
 *
 * PIN is stored in localStorage keyed by Clerk user ID so that different
 * parent accounts on the same device maintain separate PINs.
 *
 * Security note: This PIN protects against a child tapping back into adult
 * features, not against a malicious actor with localStorage access. Plain
 * string storage is acceptable for this threat model.
 */
export function usePinGuard(clerkUserId: string | null | undefined) {
  const hasPin = useCallback((): boolean => {
    if (!clerkUserId) return false;
    return !!localStorage.getItem(storageKey(clerkUserId));
  }, [clerkUserId]);

  const verify = useCallback(
    (pin: string): boolean => {
      if (!clerkUserId) return false;
      return localStorage.getItem(storageKey(clerkUserId)) === pin;
    },
    [clerkUserId]
  );

  const setPin = useCallback(
    (pin: string): void => {
      if (!clerkUserId) return;
      localStorage.setItem(storageKey(clerkUserId), pin);
    },
    [clerkUserId]
  );

  const clearPin = useCallback((): void => {
    if (!clerkUserId) return;
    localStorage.removeItem(storageKey(clerkUserId));
  }, [clerkUserId]);

  return { hasPin, verify, setPin, clearPin };
}
